type ArrayPipelineUrlFilterKey<TFilters extends object> = {
  [Key in keyof TFilters]-?: NonNullable<TFilters[Key]> extends string[] ? Key : never;
}[keyof TFilters];

type BooleanPipelineUrlFilterKey<TFilters extends object> = {
  [Key in keyof TFilters]-?: NonNullable<TFilters[Key]> extends boolean ? Key : never;
}[keyof TFilters];

type StringPipelineUrlFilterKey<TFilters extends object> = {
  [Key in keyof TFilters]-?: NonNullable<TFilters[Key]> extends string ? Key : never;
}[keyof TFilters];

type PipelineUrlCodecConfig<TFilters extends object> = {
  arrayKeys: Partial<Record<ArrayPipelineUrlFilterKey<TFilters>, string>>;
  booleanKeys?: Partial<Record<BooleanPipelineUrlFilterKey<TFilters>, string>>;
  stringKeys?: Partial<Record<StringPipelineUrlFilterKey<TFilters>, string>>;
  activeStringKeys?: Array<StringPipelineUrlFilterKey<TFilters>>;
};

export const createPipelineUrlFilterCodec = <TFilters extends object>({
  arrayKeys,
  booleanKeys = {},
  stringKeys = {},
  activeStringKeys = [],
}: PipelineUrlCodecConfig<TFilters>) => {
  const encode = (filters: TFilters): URLSearchParams => {
    const params = new URLSearchParams();

    for (const [filterKey, urlKey] of Object.entries(arrayKeys) as Array<
      [ArrayPipelineUrlFilterKey<TFilters>, string]
    >) {
      const value = filters[filterKey];
      if (Array.isArray(value) && value.length) {
        params.set(urlKey, [...value].sort().join(','));
      }
    }

    for (const [filterKey, urlKey] of Object.entries(booleanKeys) as Array<
      [BooleanPipelineUrlFilterKey<TFilters>, string]
    >) {
      if (filters[filterKey]) params.set(urlKey, 'true');
    }
    for (const [filterKey, urlKey] of Object.entries(stringKeys) as Array<
      [StringPipelineUrlFilterKey<TFilters>, string]
    >) {
      const value = filters[filterKey];
      if (typeof value === 'string' && value.trim()) {
        params.set(urlKey, value.trim());
      }
    }

    return params;
  };

  const decode = (input: URLSearchParams | string): TFilters => {
    const params = typeof input === 'string' ? new URLSearchParams(input) : input;
    const filters = {} as Partial<TFilters>;

    for (const [filterKey, urlKey] of Object.entries(arrayKeys) as Array<
      [ArrayPipelineUrlFilterKey<TFilters>, string]
    >) {
      filters[filterKey] = getArray(params, urlKey) as TFilters[typeof filterKey];
    }
    for (const [filterKey, urlKey] of Object.entries(booleanKeys) as Array<
      [BooleanPipelineUrlFilterKey<TFilters>, string]
    >) {
      filters[filterKey] = (
        params.has(urlKey) ? params.get(urlKey) === 'true' : undefined
      ) as TFilters[typeof filterKey];
    }
    for (const [filterKey, urlKey] of Object.entries(stringKeys) as Array<
      [StringPipelineUrlFilterKey<TFilters>, string]
    >) {
      filters[filterKey] = (params.get(urlKey) ?? undefined) as TFilters[typeof filterKey];
    }

    return filters as TFilters;
  };

  const getActiveFilterCount = (filters: TFilters): number => {
    let count = 0;

    for (const filterKey of Object.keys(arrayKeys) as Array<ArrayPipelineUrlFilterKey<TFilters>>) {
      const value = (filters[filterKey] ?? []) as string[];
      if (value.length > 0) count += 1;
    }
    for (const filterKey of Object.keys(booleanKeys) as Array<
      BooleanPipelineUrlFilterKey<TFilters>
    >) {
      if (filters[filterKey]) count += 1;
    }
    for (const filterKey of activeStringKeys) {
      const value = filters[filterKey];
      if (typeof value === 'string' && value.trim()) count += 1;
    }

    return count;
  };

  const ownedKeys: string[] = [
    ...Object.values(arrayKeys),
    ...Object.values(booleanKeys),
    ...Object.values(stringKeys),
  ].filter((key): key is string => typeof key === 'string');

  return {
    encode,
    decode,
    getActiveFilterCount,
    ownedKeys,
  };
};

export const togglePipelineArrayFilterValue = <
  TFilters extends object,
  TKey extends ArrayPipelineUrlFilterKey<TFilters>,
>(
  filters: TFilters,
  key: TKey,
  value: string,
): TFilters => {
  const currentValue = (filters[key] ?? []) as string[];
  const nextValue = currentValue.includes(value)
    ? currentValue.filter((item) => item !== value)
    : [...currentValue, value].sort();

  return {
    ...filters,
    [key]: nextValue,
  } as TFilters;
};

const getArray = (params: URLSearchParams, key: string): string[] => {
  const value = params.get(key);
  if (!value) return [];
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .sort();
};
